import { LanguageService } from '../shared/services/language.service';
import { EppService } from './epp.service';

import { GetLegendPipe } from '../pipes/get-legend.pipe';
import { HasLegendPipe } from '../pipes/has-legend.pipe';
import { DatexPipe } from '../shared/datex.pipe';
import { ColorMappingPipe } from './pipes/color-mapping.pipe';
import { ParameterColorPipe } from './pipes/paremeter-color.pipe';

import { WmIconDirective } from '../shared/wm-icon.directive';

import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  signal,
  untracked,
} from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

import { TippyDirective } from '@ngneat/helipopper';
import moment from 'moment';

export const MODULES = [CommonModule, TranslateModule];
export const PIPES = [
  DatexPipe,
  HasLegendPipe,
  GetLegendPipe,
  ColorMappingPipe,
  ParameterColorPipe,
];
export const DIRECTIVES = [TippyDirective, WmIconDirective];

@Component({
  selector: 'app-epp-matrix',
  templateUrl: './epp-matrix.component.html',
  styleUrls: ['./epp-matrix.component.scss', './epp-color-mapping.scss'],
  imports: [...MODULES, ...PIPES, ...DIRECTIVES],
})
export class EppMatrixComponent implements OnInit {
  private injector = inject(Injector);
  public eppService = inject(EppService);
  public languageService = inject(LanguageService);

  parameterType = input<string>();
  tableData = input<any>();
  userParameters = input<any>();
  parameterChanged = input<number>();
  parameterDefinitions = input<any>();
  resolution = input<any>();
  dashboardView = input<boolean>(false);
  parametersetName = input<string>();

  parameters = signal<string[]>(undefined);

  constructor() {
    this.listenToChanges();
  }

  ngOnInit(): void {
    this.parameters.set([...this.extractParameters()]);
  }

  extractParameters(): Array<string> {
    const parameters = [];
    const parameterType = this.parameterType();
    for (const parameter of this.userParameters()) {
      if (this.isHiddenParameter(parameter.parameter)) continue;
      if (this.parameterClass(parameter.parameter) === parameterType) {
        if (parameterType === 'index') {
          if (this.checkIfDataOfParameterExist(parameter.parameter)) {
            parameters.push(parameter.parameter);
          }
        } else {
          parameters.push(parameter.parameter);
        }
      }
    }
    return parameters;
  }

  checkIfDataOfParameterExist(parameter: string): boolean {
    for (const dataset of this.tableData()) {
      if (dataset.hasOwnProperty(parameter)) {
        if (this.isNonNeutralIndexValue(parameter, dataset[parameter])) {
          return true;
        }
      }
    }
    return false;
  }

  private isNonNeutralIndexValue(parameter: string, value: unknown): boolean {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return false;
    }

    if (parameter === 'i_gm') {
      return numericValue !== 1;
    }

    if (parameter === 'i_uv') {
      return numericValue >= 3;
    }

    return numericValue !== 0;
  }

  isGroup(index, item): boolean {
    return item.hasOwnProperty('isGroupBy') && item.isGroupBy;
  }

  isNight(dtg: string): boolean {
    return moment(dtg).utc().hours() === 6;
  }

  parameterClass(parameter: string): string {
    const coloredTemperatureParameters = [
      'tt',
      'road_rst',
      'bridge_rst',
      'bike_rst',
      'foot_rst',
      'blade_rst',
      'tx',
      'tn',
    ];
    if (coloredTemperatureParameters.indexOf(parameter) >= 0) {
      return 'tt_color';
    } else if (parameter.indexOf('tt') >= 0 || parameter === 'td') {
      return 'tt';
    }
    if (parameter.indexOf('i_') === 0) {
      return 'index';
    }
    // parameter === 'wwc' || parameter === 'wwp' || parameter === 'wwt'
    const possibilityParameters = ['wwc', 'wwp', 'wwt', 'wws'];
    if (possibilityParameters.indexOf(parameter) >= 0) {
      return 'possibility';
    }
    // condition
    const conditionParameters = [
      'road_rsc',
      'bridge_rsc',
      'bike_rsc',
      'foot_rsc',
      'rail_condition',
      'overhead_condition',
      'wing_rsc',
      'blade_rsc',
      'panel_rsc',
    ];
    if (conditionParameters.indexOf(parameter) >= 0) {
      return 'condition';
    }
    return '';
  }

  isHiddenParameter(parameter: string): boolean {
    if (parameter === 'dtg' || parameter === 'dayNight') {
      return false;
    }
    if (!this.isParameterAvailableForResolution(parameter)) {
      return true;
    }
    for (const userParameter of this.userParameters()) {
      if (userParameter.parameter === parameter) {
        return !userParameter.selected;
      }
    }
    return true;
  }

  isParameterAvailableForResolution(parameter: string): boolean {
    const hourly = this.resolution().hourly;
    return this.parameterDefinitions().some(
      p => parameter === p.fe_parameter && hourly === p.hourly
    );
  }

  private listenToChanges() {
    effect(
      () => {
        this.parameterType();
        this.tableData();
        this.userParameters();
        this.parameterChanged();
        this.parameterDefinitions();
        this.resolution();
        this.dashboardView();
        this.parametersetName();

        untracked(() => {
          this.parameters.set(null);
          this.parameters.set([...this.extractParameters()]);
        });
      },
      { injector: this.injector }
    );
  }
}
